/**
 * Exam Termination Report Page
 *
 * Displays a comprehensive report when an exam is terminated due to violations
 */

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  XCircle,
  AlertTriangle,
  Clock,
  Camera,
  Monitor,
  Clipboard,
  Eye,
  ChevronRight,
  Home,
  FileText,
} from 'lucide-react';
import { formatExamTime } from '@/utils';

interface ViolationRecord {
  id: string;
  type: string;
  timestamp: Date | string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface TerminationData {
  examTitle: string;
  examId: string;
  attemptId: string;
  terminatedAt: string;
  totalViolations: number;
  maxViolations: number;
  violations: ViolationRecord[];
  timeSpent: number; // seconds
  questionsAnswered: number;
  totalQuestions: number;
}

const VIOLATION_ICONS: Record<string, React.ReactNode> = {
  tab_switch: <Monitor className="w-5 h-5" />,
  window_blur: <Monitor className="w-5 h-5" />,
  copy_paste: <Clipboard className="w-5 h-5" />,
  fullscreen_exit: <Monitor className="w-5 h-5" />,
  face_not_detected: <Camera className="w-5 h-5" />,
  multiple_faces: <Eye className="w-5 h-5" />,
  looking_away: <Eye className="w-5 h-5" />,
  mobile_detected: <AlertTriangle className="w-5 h-5" />,
  devtools_open: <AlertTriangle className="w-5 h-5" />,
  screenshot_attempt: <AlertTriangle className="w-5 h-5" />,
  audio_detected: <AlertTriangle className="w-5 h-5" />,
  default: <AlertTriangle className="w-5 h-5" />,
};

const VIOLATION_LABELS: Record<string, string> = {
  tab_switch: 'Tab Switch Detected',
  window_blur: 'Left Exam Window',
  copy_paste: 'Copy / Paste',
  fullscreen_exit: 'Exited Fullscreen',
  face_not_detected: 'Face Not Visible',
  multiple_faces: 'Multiple Faces Detected',
  looking_away: 'Looking Away',
  mobile_detected: 'Phone Detected',
  devtools_open: 'Developer Tools Opened',
  screenshot_attempt: 'Screenshot Attempt',
  audio_detected: 'Audio Detected',
  camera_disabled: 'Camera Disabled',
  mic_disabled: 'Microphone Disabled',
  default: 'Violation',
};

const SEVERITY_STYLES = {
  low: {
    bg: 'bg-cyan-500/20',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400',
  },
  medium: {
    bg: 'bg-amber-500/20',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
  },
  high: {
    bg: 'bg-orange-500/20',
    border: 'border-orange-500/30',
    text: 'text-orange-400',
  },
  critical: {
    bg: 'bg-red-500/20',
    border: 'border-red-500/30',
    text: 'text-red-400',
  },
};

export const ExamTerminated = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const terminationData = location.state as TerminationData | undefined;

  useEffect(() => {
    // Exit fullscreen if still active
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.error);
    }
  }, []);

  // If no termination data, redirect to dashboard
  useEffect(() => {
    if (!terminationData) {
      // Show default message or redirect
      const timer = setTimeout(() => {
        navigate('/student/dashboard');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [terminationData, navigate]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };

  const formatTimestamp = (timestamp: Date | string) => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (!terminationData) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="glass-card p-8 max-w-lg w-full text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Exam Session Ended</h1>
          <p className="text-white/70 mb-6">
            Your exam session has ended. You will be redirected to the dashboard shortly.
          </p>
          <button
            onClick={() => navigate('/student/dashboard')}
            className="px-6 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:shadow-glow-teal transition-all"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="glass-card p-4 sm:p-8 mb-4 sm:mb-6">
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-red-500/20 border-2 border-red-500/30 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-white text-center mb-2">
            Exam Terminated
          </h1>
          <p className="text-red-400 text-center text-lg mb-6">
            Your exam has been terminated due to multiple proctoring violations
          </p>

          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
            <p className="text-red-400">
              <strong>Maximum violations exceeded:</strong> {terminationData.totalViolations} of {terminationData.maxViolations} allowed violations recorded
            </p>
          </div>
        </div>

        {/* Exam Details */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-400" />
            Exam Information
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4">
              <div className="text-sm text-white/60 mb-1">Exam</div>
              <div className="text-white font-medium">{terminationData.examTitle}</div>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4">
              <div className="text-sm text-white/60 mb-1">Terminated At</div>
              <div className="text-white font-medium">
                {formatExamTime(terminationData.terminatedAt)}
              </div>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4">
              <div className="text-sm text-white/60 mb-1">Time Spent</div>
              <div className="text-white font-medium flex items-center gap-2">
                <Clock className="w-4 h-4 text-white/60" />
                {formatTime(terminationData.timeSpent)}
              </div>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4">
              <div className="text-sm text-white/60 mb-1">Progress</div>
              <div className="text-white font-medium">
                {terminationData.questionsAnswered} / {terminationData.totalQuestions} questions
              </div>
            </div>
          </div>
        </div>

        {/* Violation Timeline */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Violation Report
          </h2>

          <p className="text-white/60 text-sm mb-6">
            The following violations were recorded during your exam session. Each violation
            was warned before termination.
          </p>

          <div className="space-y-3">
            {terminationData.violations.map((violation, index) => {
              const styles = SEVERITY_STYLES[violation.severity] || SEVERITY_STYLES.medium;
              const icon = VIOLATION_ICONS[violation.type] || VIOLATION_ICONS.default;
              const label = VIOLATION_LABELS[violation.type] || 'Violation';

              return (
                <div
                  key={violation.id || index}
                  className={`flex items-start gap-4 p-4 rounded-lg border ${styles.bg} ${styles.border}`}
                >
                  <div className={`mt-0.5 ${styles.text}`}>
                    {icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${styles.text}`}>
                          Warning #{index + 1}: {label}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles.bg} ${styles.text} border ${styles.border}`}>
                          {violation.severity.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-white/50 text-sm">
                        {formatTimestamp(violation.timestamp)}
                      </span>
                    </div>
                    <p className="text-white/70 text-sm mt-1">
                      {violation.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Final Warning Notice */}
          <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">
              <strong>Final Violation:</strong> After exceeding the allowed {terminationData.maxViolations} warnings,
              your exam was automatically terminated. This action cannot be undone.
            </p>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">What Happens Next?</h2>
          <div className="space-y-4 text-white/70">
            <div className="flex items-start gap-3">
              <ChevronRight className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p>Your exam attempt has been recorded as terminated due to proctoring violations.</p>
            </div>
            <div className="flex items-start gap-3">
              <ChevronRight className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p>Your instructor will be notified of this termination and may review the recorded violations.</p>
            </div>
            <div className="flex items-start gap-3">
              <ChevronRight className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p>If you believe this termination was in error, please contact your instructor or support.</p>
            </div>
            <div className="flex items-start gap-3">
              <ChevronRight className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p>Depending on your exam settings, you may or may not have additional attempts available.</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/student/dashboard')}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:shadow-glow-teal transition-all"
          >
            <Home className="w-5 h-5" />
            Return to Dashboard
          </button>
          <button
            onClick={() => navigate('/student/browse-exams')}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors"
          >
            <FileText className="w-5 h-5" />
            View Other Exams
          </button>
        </div>

        {/* Support Notice */}
        <div className="mt-8 text-center">
          <p className="text-white/50 text-sm">
            Need help? Contact support or your instructor for assistance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExamTerminated;
